import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function SpeakingPage() {
  useEffect(() => {
    document.title = 'Speaking Engagements | Henry Adebisi';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Henry shares practical lessons from over a decade of financial leadership and digital innovation — helping entrepreneurs, policymakers, and investors navigate Africa\'s evolving business landscape.');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://henryadebisi.com'}/speaking`);
    }
    
    const keywords = document.querySelector('meta[name="keywords"]');
    if (keywords) {
      keywords.setAttribute('content', 'speaking engagements, financial leadership, digital innovation, entrepreneurs, business landscape');
    }

    // Add Schema.org JSON-LD
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Speaking Engagements | Henry Adebisi",
      "description": "Henry shares practical lessons from over a decade of financial leadership and digital innovation — helping entrepreneurs, policymakers, and investors navigate Africa's evolving business landscape.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://henryadebisi.com'}/speaking`,
      "mainEntity": {
        "@type": "Person",
        "name": "Henry Olamide Adebisi",
        "hasOccupation": {
          "@type": "Occupation",
          "name": "Professional Speaker",
          "skills": [
            "Strategic Finance for Growth",
            "Fintech and the Real Economy",
            "SME Funding and Investor Readiness",
            "Cross-border Financial Operations"
          ]
        }
      }
    };

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify(schema);
    document.head.appendChild(schemaScript);

    return () => {
      const schemaToRemove = document.querySelector('script[type="application/ld+json"]');
      if (schemaToRemove) {
        schemaToRemove.remove();
      }
    };
  }, []);

  const engagements = [
    {
      title: 'Guest Appearance - News Central TV',
      description: 'Discussing fintech trends and financial inclusion across African markets',
      url: 'https://www.youtube.com/watch?v=jfev7t_JX0A',
      type: 'TV Interview',
      platform: 'News Central TV'
    },
    {
      title: 'Simplified Financial Planning for SMEs, Entrepreneurs & Freelancers',
      description: 'Comprehensive webinar on practical financial planning strategies for small businesses',
      url: 'https://www.linkedin.com/events/simplifiedfinancialplanningfors7057693639384190976/theater/',
      type: 'Webinar',
      platform: 'Online Event'
    }
  ];

  const topics = [
    {
      title: 'Strategic Finance for Growth',
      description: 'Building financial frameworks that enable sustainable business expansion across African markets',
      icon: 'ri-line-chart-line'
    },
    {
      title: 'Fintech and the Real Economy',
      description: 'Bridging the gap between financial technology innovation and real-world business applications',
      icon: 'ri-smartphone-line'
    },
    {
      title: 'SME Funding and Investor Readiness',
      description: 'Preparing small and medium enterprises for investment and scaling opportunities',
      icon: 'ri-funds-line'
    },
    {
      title: 'Cross-border Financial Operations',
      description: 'Managing financial systems and compliance across multiple African jurisdictions',
      icon: 'ri-global-line'
    },
    {
      title: 'Partnership Management',
      description: 'Building and managing strategic partnerships with international development organizations',
      icon: 'ri-team-line'
    },
    {
      title: 'Financial Transformation',
      description: 'Leading organizational change and profitability improvements in challenging environments',
      icon: 'ri-refresh-line'
    }
  ];

  return (
    <div className="min-h-screen bg-off-white">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section 
          className="py-32 bg-cover bg-center relative"
          style={{
            backgroundImage: `linear-gradient(rgba(10, 37, 64, 0.8), rgba(10, 37, 64, 0.8)), url('https://public.readdy.ai/ai/img_res/2434ca947f4d174dc426c211381d1b16.jpg')`
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="font-sora font-bold text-4xl lg:text-5xl text-white mb-6">
              Speaking Engagements
            </h1>
            <p className="text-gray-200 text-lg max-w-3xl mx-auto leading-relaxed">
              Henry shares insights on finance transformation, fintech innovation, SME funding, and business leadership 
              with audiences across Africa and globally.
            </p>
          </div>
        </section>

        {/* Speaking Topics */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-sora font-bold text-3xl lg:text-4xl text-navy mb-4">
                Key Speaking Topics
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Henry brings practical insights and real-world experience to every speaking engagement
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {topics.map((topic, index) => (
                <div key={index} className="bg-off-white rounded-lg p-8 hover:shadow-lg transition-shadow duration-300">
                  <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mb-6">
                    <i className={`${topic.icon} text-2xl text-gold`}></i>
                  </div>
                  <h3 className="font-sora font-semibold text-xl text-navy mb-4">
                    {topic.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {topic.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Past Engagements */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-sora font-bold text-3xl lg:text-4xl text-navy mb-4">
                Recent Engagements
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Highlights from Henry's recent speaking appearances and thought leadership contributions
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {engagements.map((engagement, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <div className="aspect-video bg-gray-200 relative">
                    {engagement.title === 'Simplified Financial Planning for SMEs, Entrepreneurs & Freelancers' ? (
                      <img
                        src="https://static.readdy.ai/image/6b84a11e410079b56c6291a27cbf72c2/c538b9bf7eedfcc403b1dd897aeb9706.png"
                        alt={engagement.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <iframe
                        src={engagement.url.replace('watch?v=', 'embed/')}
                        title={engagement.title}
                        className="w-full h-full"
                        allowFullScreen
                      ></iframe>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-gold font-semibold text-sm">
                        {engagement.type}
                      </span>
                      <span className="text-gray-500 text-sm">
                        {engagement.platform}
                      </span>
                    </div>
                    
                    <h3 className="font-sora font-bold text-xl text-navy mb-3">
                      {engagement.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {engagement.description}
                    </p>
                    
                    <a 
                      href={engagement.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gold hover:text-navy font-semibold transition-colors duration-200"
                    >
                      {engagement.title === 'Simplified Financial Planning for SMEs, Entrepreneurs & Freelancers' ? 'View Event Details' : 'Watch Full Video'} <i className="ri-external-link-line ml-1"></i>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Speaking Request Form */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-sora font-bold text-3xl text-navy mb-6">
                  Invite Henry to Speak
                </h2>
                <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                  Looking for an expert speaker on African finance, fintech innovation, or strategic business growth? 
                  Henry brings practical insights and engaging presentations to conferences, webinars, and corporate events.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center text-gray-600">
                    <i className="ri-check-line text-gold mr-3"></i>
                    <span>Keynote presentations</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <i className="ri-check-line text-gold mr-3"></i>
                    <span>Panel discussions</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <i className="ri-check-line text-gold mr-3"></i>
                    <span>Webinars and virtual events</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <i className="ri-check-line text-gold mr-3"></i>
                    <span>Corporate workshops</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-off-white rounded-lg p-8">
                <form className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Name *
                    </label>
                    <input 
                      type="text" 
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                      placeholder="Conference or event name"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Name *
                      </label>
                      <input 
                        type="text" 
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                        placeholder="Full name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input 
                        type="email" 
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Event Date
                      </label>
                      <input 
                        type="date" 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Event Format
                      </label>
                      <select className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent">
                        <option>Select format</option>
                        <option>Keynote</option>
                        <option>Panel Discussion</option>
                        <option>Webinar</option>
                        <option>Workshop</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Details *
                    </label>
                    <textarea 
                      required
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent resize-none"
                      placeholder="Please provide details about your event, audience, preferred topics, and any specific requirements..."
                      maxLength={500}
                    ></textarea>
                    <p className="text-gray-500 text-sm mt-1">Maximum 500 characters</p>
                  </div>
                  
                  <Button variant="secondary" size="lg" className="w-full">
                    Send Speaking Request
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
